import PropTypes from 'prop-types';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { formatValue } from './sosShared';

export default function SosSignalActionDialog({
  open,
  mode,
  signal,
  draft,
  error,
  submitting,
  onDraftChange,
  onClose,
  onSubmit,
}) {
  const config = {
    resolve: {
      title: 'Resolve SOS Signal',
      confirmLabel: 'Resolve Signal',
      confirmColor: 'success',
      description:
        'This resolves the SOS signal and any linked SOS review case, and clears SOS-created outward-transfer restrictions.',
    },
    extend: {
      title: 'Extend Monitoring',
      confirmLabel: 'Extend Monitoring',
      confirmColor: 'warning',
      description:
        'This updates the SOS expiry and extends any active SOS outward-transfer block to the same expiry.',
    },
    remove_block: {
      title: 'Remove SOS Block',
      confirmLabel: 'Remove Block',
      confirmColor: 'error',
      description:
        'This removes the outward-transfer restriction without resolving the SOS signal or its linked case.',
    },
  }[mode];

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{config.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {config.description}
          </Typography>

          {signal ? (
            <Alert severity="info" variant="outlined">
              Signal #{signal.id} for user {formatValue(signal.user_id)} is currently {signal.status}.
            </Alert>
          ) : null}

          {mode === 'extend' ? (
            <DateTimePicker
              label="New expiry"
              value={draft.expires_at}
              onChange={(value) => onDraftChange('expires_at', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  required
                  error={Boolean(params.error)}
                  helperText={
                    params.error
                      ? 'Select a valid monitoring expiry.'
                      : 'The selected datetime will be formatted to the server contract on submit.'
                  }
                />
              )}
            />
          ) : null}

          <TextField
            label="Note"
            value={draft.note}
            onChange={(event) => onDraftChange('note', event.target.value)}
            multiline
            minRows={3}
            fullWidth
            required
            helperText="A note is required for auditability."
          />

          {error ? (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color={config.confirmColor}
          onClick={onSubmit}
          loading={submitting}
          disabled={!draft.note.trim() || (mode === 'extend' && !draft.expires_at)}
        >
          {config.confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

SosSignalActionDialog.propTypes = {
  draft: PropTypes.shape({
    expires_at: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
      PropTypes.oneOf([null]),
    ]),
    note: PropTypes.string,
  }).isRequired,
  error: PropTypes.string,
  mode: PropTypes.oneOf(['resolve', 'extend', 'remove_block']),
  onClose: PropTypes.func.isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  signal: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.string,
    user_id: PropTypes.string,
  }),
  submitting: PropTypes.bool.isRequired,
};
